import React, { useRef, useEffect, useState } from "react";
import {
  Dialog,
  Box,
  Typography,
  useTheme,
  IconButton,
  Chip,
  Divider,
  DialogContent,
  DialogTitle,
  Button,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import LaunchIcon from "@mui/icons-material/Launch";
import ControlDetailDialog from "../../../Settings/Frameworks/FrameWorkDialogs/ControlDetailsDialog";
import { debounce } from "lodash";
import ReadinessChip from "../../../../components/ReadinessChip/ReadinessChip";

const MappedControlDialog = ({ open, onClose, data, refetchData }) => {
  const theme = useTheme();
  const [selectedControl, setSelectedControl] = useState(null); // Track by controlsId
  const [currentControl, setCurrentControl] = useState({});
  const [isOpen, setIsOpen] = useState(false);
  const sectionRefs = useRef({});
  const rightContainerRef = useRef();

  const sortedControls = [...(data?.mappedControl || [])].sort(
    (a, b) => a.controlsId - b.controlsId
  );

  useEffect(() => {
    // Populate refs for sections
    const refs = {};
    sortedControls.forEach((_, i) => {
      refs[sortedControls[i].controlsId] = React.createRef();
    });
    sectionRefs.current = refs;

    // Force initial synchronization
    if (open && sortedControls.length > 0) {
      setTimeout(() => handleScrollTo(sortedControls[0].controlsId), 100); // Delay to ensure refs are ready
    }
  }, [data, open]);

  // Sync currentControl with selectedControl index
  useEffect(() => {
    if (sortedControls.length > 0 && selectedControl !== null) {
      const found = sortedControls.find(
        (ctrl) => ctrl.controlsId === selectedControl
      );
      setCurrentControl(found || {});
    } else {
      setCurrentControl({});
    }
  }, [selectedControl, sortedControls]);

  const handleScrollTo = (id) => {
    setSelectedControl(id);

    const section = sectionRefs.current[id]?.current;
    const container = rightContainerRef.current;

    if (section && container) {
      const sectionTop = section.offsetTop;
      const sectionHeight = section.offsetHeight;
      const containerScrollTop = container.scrollTop;
      const containerHeight = container.offsetHeight;

      // Scroll only if the section is not fully visible
      if (
        sectionTop < containerScrollTop || // Section is above visible area
        sectionTop + sectionHeight > containerScrollTop + containerHeight // Section is below visible area
      ) {
        container.scrollTo({
          top: sectionTop - 80, // Align section to the top of the container
          behavior: "smooth",
        });
      }
    }
  };

  const handleScroll = debounce(() => {
    if (!rightContainerRef.current) return;

    const containerTop = rightContainerRef.current.getBoundingClientRect().top;
    let selectedId =
      sortedControls.length > 0 ? sortedControls[0].controlsId : null;

    sortedControls.forEach((item) => {
      const section = sectionRefs.current[item.controlsId]?.current;
      if (section) {
        const sectionTop = section.getBoundingClientRect().top;
        if (sectionTop - containerTop <= 30) {
          selectedId = item.controlsId;
        }
      }
    });

    if (selectedControl !== selectedId) {
      setSelectedControl(selectedId);

      // Scroll corresponding button into view
      const leftButton = document.querySelector(
        `[data-control-id="${selectedId}"]`
      );
      if (leftButton) {
        leftButton.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }
  }, 100); // Debounce to reduce frequent updates

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle fontWeight="bold">
        Controls Mapped to {data?.area}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{ overflow: "hidden", padding: "unset !important" }}
      >
        <Box display="flex" flexDirection="row" height="560px">
          {/* Left Side Controls */}
          <Box
            width="10%"
            borderRight={`1px solid ${theme.palette.divider}`}
            padding={2}
            bgcolor={theme.palette.grey[100]}
            height="100%"
            overflow="auto"
          >
            <Typography variant="h5" marginBottom={2}>
              Controls
            </Typography>
            <Box
              display="flex"
              flexDirection="column"
              gap={1}
              paddingBottom="30px"
            >
              {sortedControls.map((item, i) => (
                <Button
                  key={item.controlsId}
                  data-control-id={item.controlsId}
                  onClick={() => handleScrollTo(item.controlsId)}
                  variant={
                    selectedControl === item.controlsId
                      ? "contained"
                      : "outlined"
                  }
                  sx={{
                    justifyContent: "flex-start",
                    textTransform: "none",
                    fontWeight:
                      selectedControl === item.controlsId ? "bold" : "normal",
                    color:
                      selectedControl === item.controlsId ? "white" : "black",
                  }}
                >
                  {item.controlsName}
                </Button>
              ))}
            </Box>
          </Box>

          {/* Right Side Content */}
          <Box
            width="90%"
            padding={2}
            bgcolor={theme.palette.grey[100]}
            height="100%"
            sx={{ overflow: "auto" }}
            ref={rightContainerRef}
            onScroll={handleScroll}
          >
            {sortedControls.map((item, i) => (
              <Box
                key={item.controlsId}
                ref={sectionRefs.current[i]}
                marginBottom={4}
                border="1px solid"
                borderColor={theme.palette.divider}
                borderRadius={2}
                padding={2}
                boxShadow={1}
                bgcolor="#ffff"
                sx={{ scrollMarginTop: "15px" }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  marginBottom={2}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "15px",
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{ flexGrow: 1, fontWeight: "bold" }}
                    >
                      {item?.controlsName} : {item?.controlsTitle}
                    </Typography>
                    <Typography
                      variant="p"
                      sx={{
                        flexGrow: 1,
                        fontWeight: "bold",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "5px",
                      }}
                      color="primary"
                      onClick={() => {
                        setIsOpen(true);
                        setSelectedControl(item.controlsId);
                      }}
                    >
                      View Details <LaunchIcon fontSize="small" />
                    </Typography>
                  </Box>
                  <ReadinessChip percentage={item?.readinessPercentage || 0} />
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {item?.controlsDescription}
                </Typography>
                <Divider sx={{ marginY: 2 }} />
                {/*
                  {item.checks?.map((check) => (
                    <Box
                      key={check.checksId}
                      display="flex"
                      alignItems="center"
                      gap={2}
                      marginY={1}
                    >
                      <Chip
                        label={
                          check?.checksCategory === "system_check"
                          ? "AUTOMATED"
                          : "WORKFLOW"
                        }
                        variant="outlined"
                      />
                      <Typography variant="subtitle1" fontWeight="bold">
                        {check?.checkDescription ?? check.activeCheckDescription}
                      </Typography>
                    </Box>
                  ))}
                */}
              </Box>
            ))}
          </Box>
        </Box>
      </DialogContent>
      {isOpen && (
        <ControlDetailDialog
          open={isOpen}
          onClose={() => setIsOpen(false)}
          control={currentControl}
          refetchData={refetchData}
        />
      )}
    </Dialog>
  );
};

export default MappedControlDialog;
